import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, Mail, Save, IndianRupee, Eye, EyeOff, Copy, Plus, Check, Clock, X as XIcon, MinusCircle, ChevronLeft, ChevronRight, Trash2, Send, UserCog, Link as LinkIcon, BookOpen, TrendingDown, TrendingUp, FileText, IdCard, Download, Pencil, Loader2, Receipt } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api, API, waLink, waToCustomer, getErr, safeCopy } from "../../lib/api";
import DocumentViewer from "../../components/DocumentViewer";
import PortalCredentialsCard from "../../components/PortalCredentialsCard";
import EditRecordDialog from "../../components/EditRecordDialog";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const STATUS_META = {
  "Done":           { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: Check,        label: "Done" },
  "Pending":        { cls: "bg-amber-100  text-amber-700  border-amber-200",     icon: Clock,        label: "Pending" },
  "Not Done":       { cls: "bg-red-100    text-red-700    border-red-200",       icon: XIcon,        label: "Not Done" },
  "Not Applicable": { cls: "bg-slate-100  text-slate-600  border-slate-200",     icon: MinusCircle,  label: "N/A" },
};
const STATUS_CYCLE = ["", "Done", "Pending", "Not Done", "Not Applicable"];

export default function AdminCustomerDetail() {
  const { customerId } = useParams();
  const [customer, setCustomer] = useState(null);
  const [pay, setPay] = useState({ amount: "", method: "UPI", note: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [addServiceId, setAddServiceId] = useState("");
  const [allServices, setAllServices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const [genLink, setGenLink] = useState(false);
  const [waBusy, setWaBusy] = useState(false);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const onDelete = async () => {
    if (!window.confirm(`Delete customer ${customer.ref} (${customer.full_name})?\n\nThis removes the customer record, their payment history and credentials. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/customers/${customerId}`);
      toast.success("Customer deleted");
      navigate("/admin/customers");
    } catch (e) { toast.error(getErr(e, "Delete failed")); }
    finally { setDeleting(false); }
  };

  const load = () => api.get(`/admin/customers/${customerId}`).then((r) => {
    setCustomer(r.data);
    setAssignee(r.data.assigned_to || "");
    setPaymentLink(r.data.payment_link || "");
  });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);
  useEffect(() => { api.get("/services").then((r) => setAllServices(r.data.services)); }, []);
  useEffect(() => { api.get("/admin/operators").then((r) => setOperators(r.data.operators)).catch(() => {}); }, []);

  const saveAssignment = async () => {
    try {
      await api.patch(`/admin/customers/${customerId}/assign`, { assigned_to: assignee || "" });
      toast.success("Assignment saved");
      load();
    } catch { toast.error("Failed"); }
  };

  const savePaymentLink = async () => {
    setSavingLink(true);
    try {
      await api.patch(`/admin/customers/${customerId}`, { payment_link: paymentLink });
      toast.success("Payment link saved");
      load();
    } catch { toast.error("Failed"); }
    finally { setSavingLink(false); }
  };

  const generateRazorpayLink = async () => {
    setGenLink(true);
    try {
      const { data } = await api.post(`/admin/customers/${customerId}/razorpay/payment-link`, {});
      setPaymentLink(data.payment_link);
      toast.success(`Razorpay link created for ₹${(data.amount || 0).toLocaleString("en-IN")}`);
      load();
      return data.payment_link;
    } catch (e) {
      toast.error(getErr(e, "Could not create Razorpay link"));
      return null;
    } finally { setGenLink(false); }
  };

  const sendRazorpayLinkOnWhatsApp = async () => {
    if ((customer.total_due || 0) <= 0) return toast.error("Nothing to collect — outstanding balance is zero");
    setWaBusy(true);
    try {
      // Always generate a fresh Razorpay link so the amount matches current due
      // and the link is webhook-tracked for successful payment reconciliation.
      const { data } = await api.post(`/admin/customers/${customerId}/razorpay/payment-link`, {});
      setPaymentLink(data.payment_link);
      const msg = buildPaymentLinkMessage(customer, data.payment_link);
      const url = waToCustomer(customer.whatsapp || customer.mobile, msg);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) toast.error("Pop-up blocked — please allow pop-ups to share on WhatsApp");
      else toast.success("Razorpay link ready — sending on WhatsApp");
      load();
    } catch (e) {
      toast.error(getErr(e, "Could not prepare Razorpay link"));
    } finally { setWaBusy(false); }
  };

  const addPayment = async () => {
    const amt = parseFloat(pay.amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post(`/admin/customers/${customerId}/payments`, { amount: amt, method: pay.method, note: pay.note });
      setPay({ amount: "", method: "UPI", note: "" });
      toast.success("Payment recorded");
      load();
    } catch { toast.error("Failed"); }
  };

  const addService = async () => {
    if (!addServiceId) return toast.error("Pick a service");
    try {
      await api.post(`/admin/customers/${customerId}/services`, { service_id: addServiceId });
      toast.success("Service added");
      setAddOpen(false);
      setAddServiceId("");
      load();
    } catch (e) { toast.error(getErr(e, "Failed to add service")); }
  };

  const removeService = async (idx) => {
    if (!window.confirm("Remove this service from customer? Payment totals are not adjusted.")) return;
    try {
      await api.delete(`/admin/customers/${customerId}/services/${idx}`);
      toast.success("Service removed");
      load();
    } catch { toast.error("Failed"); }
  };

  if (!customer) return <div className="p-10 text-[#5C6661]">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 md:p-10" data-testid="customer-detail-page">
      <Toaster richColors position="top-right" />

      <Link to="/admin/customers" className="text-sm text-[#5C6661] hover:text-[#121C17] inline-flex items-center gap-1 mb-6" data-testid="customer-detail-back">
        <ArrowLeft size={14} /> Back to customers
      </Link>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div>
          <span className="font-mono text-xs text-[#5C6661]">{customer.ref}</span>
          <h1 className="mt-1 text-3xl font-display font-medium tracking-tight text-[#121C17]">{customer.full_name}</h1>
          <p className="text-sm text-[#5C6661] mt-1">{customer.email} · {customer.mobile} · {customer.city}, {customer.state}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`tel:${customer.mobile}`} className="rounded-full px-4 py-2 text-sm font-medium border border-[#E5E2DC] bg-white inline-flex items-center gap-1"><Phone size={14} /> Call</a>
          <a href={waLink(`Hi ${customer.full_name}, this is from FinSahayak.`)} target="_blank" rel="noopener noreferrer"
            className="rounded-full px-4 py-2 text-sm font-medium text-white inline-flex items-center gap-1" style={{ background: "#25D366" }}><MessageCircle size={14} /> WhatsApp</a>
          <a href={`mailto:${customer.email}`} className="rounded-full px-4 py-2 text-sm font-medium border border-[#E5E2DC] bg-white inline-flex items-center gap-1"><Mail size={14} /> Email</a>
          <button onClick={() => setEditOpen(true)} data-testid="customer-edit"
            className="rounded-full px-4 py-2 text-sm font-medium border border-[#E5E2DC] bg-white inline-flex items-center gap-1 hover:bg-[#F3F1EC]"><Pencil size={14} /> Edit</button>
          <Link to={`/admin/customers/${customer.id}/gst`} data-testid="customer-gst-link"
            className="rounded-full px-4 py-2 text-sm font-medium text-white bg-[#1E4D3B] inline-flex items-center gap-1 hover:opacity-90">
            <Receipt size={14} /> GST Module
          </Link>
          <button onClick={onDelete} disabled={deleting} data-testid="customer-delete"
            className="rounded-full px-4 py-2 text-sm font-medium border border-red-200 text-red-700 bg-white inline-flex items-center gap-1 hover:bg-red-50 disabled:opacity-60">
            {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Delete
          </button>
        </div>
      </div>

      <EditRecordDialog
        open={editOpen}
        kind="customer"
        record={customer}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); load(); }}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <div className="bento-card p-5"><div className="text-xs label-eyebrow">Total Paid</div><div className="mt-2 text-2xl font-display font-medium text-emerald-700">₹{customer.total_paid.toLocaleString("en-IN")}</div></div>
        <div className="bento-card p-5"><div className="text-xs label-eyebrow">Outstanding</div><div className="mt-2 text-2xl font-display font-medium text-amber-700">₹{customer.total_due.toLocaleString("en-IN")}</div></div>
        <div className="bento-card p-5"><div className="text-xs label-eyebrow">Services</div><div className="mt-2 text-2xl font-display font-medium text-[#1E4D3B]">{customer.service_history?.length || 0}</div></div>
        <div className="bento-card p-5"><div className="text-xs label-eyebrow">Since</div><div className="mt-2 text-sm text-[#5C6661]">{new Date(customer.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div></div>
      </div>

      {/* ASSIGNMENT */}
      <div className="bento-card p-6 mb-6 flex flex-col md:flex-row items-start md:items-center gap-4" data-testid="customer-assign-panel">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-[#1E4D3B]/10 grid place-items-center text-[#1E4D3B]"><UserCog size={18} /></div>
          <div>
            <span className="label-eyebrow text-[10px]">Assigned operator</span>
            <div className="text-sm font-medium text-[#121C17] mt-0.5">
              {customer.assigned_to_name || <span className="text-[#5C6661] font-normal">Unassigned</span>}
            </div>
          </div>
        </div>
        <div className="flex-1 w-full md:max-w-md flex gap-2">
          <select value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="customer-assign-select"
            className="flex-1 bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 text-sm focus:border-[#1E4D3B]">
            <option value="">— Unassigned —</option>
            {operators.map((op) => (
              <option key={op.id} value={op.id}>{op.name}{!op.is_active ? " (disabled)" : ""}</option>
            ))}
          </select>
          <button onClick={saveAssignment} data-testid="customer-assign-save" className="btn-primary rounded-full px-4 py-2.5 text-sm font-medium">Save</button>
        </div>
        {operators.length === 0 && (
          <p className="text-xs text-[#5C6661] md:ml-auto">
            No operators yet. <Link to="/admin/operators" className="text-[#1E4D3B] underline">Add team members</Link>.
          </p>
        )}
      </div>

      {/* PORTAL CREDENTIALS + IDENTITY (PAN/Aadhaar) */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <PortalCredentialsCard kind="customers" id={customer.id} customerName={customer.full_name}
          whatsappTarget={customer.whatsapp || customer.mobile} portalUrl={`${window.location.origin}/login`} />
        <div className="bento-card p-6" data-testid="customer-identity">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-[#1E40AF]/15 text-[#1E40AF] grid place-items-center"><IdCard size={16} /></div>
            <div>
              <span className="label-eyebrow text-[10px]">Identity</span>
              <h3 className="font-display font-medium text-base text-[#121C17] -mt-0.5">PAN & Aadhaar</h3>
            </div>
          </div>
          <dl className="grid sm:grid-cols-2 gap-4 text-sm">
            <div><dt className="text-[10px] uppercase tracking-wider text-[#5C6661]">PAN</dt><dd className="mt-1.5 font-mono text-[#121C17]" data-testid="cust-pan">{customer.pan || "—"}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-wider text-[#5C6661]">Aadhaar</dt><dd className="mt-1.5 font-mono text-[#121C17]" data-testid="cust-aadhaar">{customer.aadhaar || "—"}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-wider text-[#5C6661]">WhatsApp</dt><dd className="mt-1.5 font-mono text-[#121C17]">+91 {customer.whatsapp || customer.mobile}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-wider text-[#5C6661]">Email</dt><dd className="mt-1.5 text-[#121C17] truncate">{customer.email || "—"}</dd></div>
          </dl>
        </div>
      </div>

      {/* CUSTOMER DOCUMENTS (top-level — inherited from lead) */}
      {customer.documents?.length > 0 && (
        <div className="bento-card p-5 sm:p-6 mb-6" data-testid="customer-documents-panel">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-[#1E4D3B]/10 text-[#1E4D3B] grid place-items-center"><FileText size={16} /></div>
            <div>
              <span className="label-eyebrow text-[10px]">Submitted documents</span>
              <h3 className="font-display font-medium text-base text-[#121C17] -mt-0.5">{customer.documents.length} file(s)</h3>
            </div>
          </div>
          <ul className="grid sm:grid-cols-2 gap-2">
            {customer.documents.map((d) => (
              <li key={d.id} className="flex items-center gap-3 p-3 rounded-xl border border-[#E5E2DC] hover:bg-[#FDFCFB]" data-testid={`cust-document-${d.id}`}>
                <FileText size={16} className="text-[#1E4D3B]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#121C17] truncate">{d.filename}</div>
                  <div className="text-xs text-[#5C6661]">{(d.size / 1024).toFixed(1)} KB · {d.content_type}</div>
                </div>
                <button onClick={() => setViewerDoc(d)} data-testid={`cust-view-${d.id}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#E5E2DC] hover:bg-[#F3F1EC] inline-flex items-center gap-1">
                  <Eye size={12} /> View
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* WHATSAPP SHORTCUTS */}
      <div className="bento-card p-5 sm:p-6 mb-6" data-testid="whatsapp-share-panel">
        <h3 className="label-eyebrow text-[10px] mb-3">Send to customer on WhatsApp</h3>

        {/* Payment link */}
        <div className="mb-4">
          <label className="label-eyebrow text-[10px] flex items-center gap-1.5"><LinkIcon size={11} /> Payment link (UPI / Razorpay / GPay collect)</label>
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <input
              value={paymentLink}
              onChange={(e) => setPaymentLink(e.target.value)}
              data-testid="payment-link-input"
              placeholder="upi://pay?pa=... or https://rzp.io/l/..."
              className="flex-1 bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 text-sm focus:border-[#1E4D3B] font-mono"
            />
            <button onClick={savePaymentLink} disabled={savingLink} data-testid="payment-link-save"
              className="rounded-xl px-4 py-2.5 text-sm font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC] disabled:opacity-60">
              {savingLink ? "Saving…" : "Save"}
            </button>
            <button onClick={generateRazorpayLink} disabled={genLink || (customer.total_due || 0) <= 0} data-testid="rzp-generate-link"
              title={(customer.total_due || 0) <= 0 ? "No outstanding balance to bill" : "Generate a Razorpay payment link for the outstanding amount"}
              className="rounded-xl px-4 py-2.5 text-sm font-medium text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
              style={{ background: "#1E4D3B" }}>
              {genLink ? "Creating…" : <>Razorpay link · ₹{(customer.total_due || 0).toLocaleString("en-IN")}</>}
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <a
            data-testid="wa-due-reminder"
            href={waToCustomer(customer.whatsapp || customer.mobile, buildDueMessage(customer))}
            target="_blank" rel="noopener noreferrer"
            className="rounded-xl px-4 py-3 text-sm font-medium inline-flex items-center justify-center gap-1.5 text-white transition-transform active:scale-95"
            style={{ background: customer.total_due > 0 ? "#25D366" : "#94a3b8" }}>
            <Send size={14} /> Send due (₹{customer.total_due.toLocaleString("en-IN")})
          </a>
          <button
            data-testid="wa-payment-link"
            onClick={() => sendRazorpayLinkOnWhatsApp()}
            disabled={waBusy || (customer.total_due || 0) <= 0}
            className="rounded-xl px-4 py-3 text-sm font-medium inline-flex items-center justify-center gap-1.5 text-white transition-transform active:scale-95 disabled:opacity-60"
            style={{ background: "#D17B5D" }}
            title={(customer.total_due || 0) <= 0 ? "No outstanding balance" : "Generate Razorpay link & share on WhatsApp"}>
            <LinkIcon size={14} /> {waBusy ? "Preparing…" : "Send Razorpay link"}
          </button>
          <a
            data-testid="wa-account-summary"
            href={waToCustomer(customer.whatsapp || customer.mobile, buildAccountSummaryMessage(customer))}
            target="_blank" rel="noopener noreferrer"
            className="rounded-xl px-4 py-3 text-sm font-medium inline-flex items-center justify-center gap-1.5 text-white transition-transform active:scale-95"
            style={{ background: "#1E4D3B" }}>
            <Send size={14} /> Send summary
          </a>
        </div>
        <p className="text-xs text-[#5C6661] mt-3">Opens WhatsApp on <span className="font-mono text-[#121C17]">+{(customer.whatsapp || customer.mobile)}</span> with a pre-filled message.</p>
      </div>

      {/* KHATABOOK / LEDGER */}
      <KhatabookSection customer={customer} />

      {/* SERVICES SECTION */}
      <div className="flex items-end justify-between mb-4">
        <div>
          <span className="label-eyebrow">Customer services</span>
          <h2 className="mt-2 text-2xl font-display font-medium tracking-tight text-[#121C17]">Credentials & monthly work status</h2>
        </div>
        <button onClick={() => setAddOpen((v) => !v)} data-testid="add-service-toggle"
          className="btn-primary rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-1">
          <Plus size={14} /> Add service
        </button>
      </div>

      {addOpen && (
        <div className="bento-card p-5 mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end" data-testid="add-service-panel">
          <div className="flex-1">
            <label className="label-eyebrow text-[10px]">Service</label>
            <select value={addServiceId} onChange={(e) => setAddServiceId(e.target.value)} data-testid="add-service-select"
              className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 text-sm focus:border-[#1E4D3B]">
              <option value="">Choose a service…</option>
              {allServices.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.price_label}</option>)}
            </select>
          </div>
          <button onClick={addService} data-testid="add-service-submit" className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium">Add to customer</button>
          <button onClick={() => setAddOpen(false)} className="rounded-full px-5 py-2.5 text-sm font-medium border border-[#E5E2DC]">Cancel</button>
        </div>
      )}

      <div className="space-y-5">
        {customer.service_history.map((svc, idx) => (
          <ServiceCard key={svc.id || idx} svc={svc} idx={idx} customerId={customerId} customer={customer} onChange={load} onRemove={() => removeService(idx)} />
        ))}
      </div>

      {/* PAYMENTS */}
      <div className="mt-8 bento-card p-6" data-testid="customer-payments">
        <h3 className="label-eyebrow text-[10px]">Payments</h3>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-6 gap-2">
          <div className="sm:col-span-2 relative">
            <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6661]" />
            <input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} placeholder="Amount" data-testid="payment-amount"
              className="w-full bg-white border border-[#E5E2DC] rounded-xl pl-9 pr-3 py-2.5 text-sm focus:border-[#1E4D3B]" />
          </div>
          <select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} data-testid="payment-method"
            className="bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B] sm:col-span-1">
            {["UPI", "Bank Transfer", "Cash", "Razorpay"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <input className="sm:col-span-2 bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B]"
            placeholder="Note (optional)" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} data-testid="payment-note" />
          <button onClick={addPayment} data-testid="payment-add" className="btn-primary rounded-xl py-2.5 text-sm font-medium sm:col-span-1">Add</button>
        </div>

        {customer.payments?.length ? (
          <ul className="mt-5 space-y-2">
            {customer.payments.slice().reverse().map((p) => {
              const receiptUrl = `${API}/receipts/${p.id}`;
              const waMsg = `Hello ${customer.full_name || "there"},\n\nThank you for your payment of *₹${p.amount.toLocaleString("en-IN")}* via ${p.method}.\n\n📄 Download your receipt:\n${receiptUrl}\n\nReference: ${customer.ref || "—"}\nDate: ${new Date(p.created_at).toLocaleDateString("en-IN")}\n\nWe appreciate your business 🙏\n— Team FinSahayak\nfinsahayak.com`;
              const waHref = waToCustomer(customer.whatsapp || customer.mobile, waMsg);
              return (
                <li key={p.id} className="p-3 rounded-xl border border-[#E5E2DC] flex flex-wrap items-center gap-3" data-testid={`payment-row-${p.id}`}>
                  <div className="flex-1 min-w-[160px]">
                    <div className="text-sm font-medium text-[#121C17]">₹{p.amount.toLocaleString("en-IN")} <span className="text-xs text-[#5C6661] font-normal">via {p.method}</span></div>
                    <div className="text-xs text-[#5C6661]">{new Date(p.created_at).toLocaleString("en-IN")} {p.note && `· ${p.note}`}</div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full pill-completed">Received</span>
                  <a href={receiptUrl} target="_blank" rel="noopener noreferrer" data-testid={`receipt-download-${p.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#1E4D3B] hover:underline">
                    <Download size={12} /> Receipt
                  </a>
                  {p.invoice_id && (
                    <Link to={`/admin/invoices/${p.invoice_id}`} data-testid={`invoice-link-${p.id}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-[#D17B5D] hover:underline">
                      <FileText size={12} /> {p.invoice_number || "Invoice"}
                    </Link>
                  )}
                  <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid={`receipt-wa-${p.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline">
                    <MessageCircle size={12} /> Send on WhatsApp
                  </a>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-4 text-sm text-[#5C6661]">No payments recorded yet.</p>
        )}
      </div>

      <DocumentViewer doc={viewerDoc} onClose={() => setViewerDoc(null)} />
    </div>
  );
}

// ============================================================================
// ServiceCard — one card per service entry: credentials + monthly grid
// ============================================================================
function ServiceCard({ svc, idx, customerId, customer, onChange, onRemove }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [creds, setCreds] = useState({
    service_username: svc.service_username || "",
    service_password: svc.service_password || "",
    status: svc.status || "Active",
    renewal_date: svc.renewal_date || "",
    notes: svc.notes || "",
  });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCreds({
      service_username: svc.service_username || "",
      service_password: svc.service_password || "",
      status: svc.status || "Active",
      renewal_date: svc.renewal_date || "",
      notes: svc.notes || "",
    });
  }, [svc]);

  const saveCreds = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/customers/${customerId}/services/${idx}`, creds);
      toast.success("Service credentials saved");
      onChange();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const copyToClipboard = async (text, label) => {
    if (!text) return;
    const ok = await safeCopy(text);
    if (ok) toast.success(`${label} copied`);
    else toast.error("Couldn't copy automatically — please select and copy manually.");
  };

  const yearMap = (svc.monthly_status || {})[String(year)] || {};

  const cycleMonth = async (monthIdx) => {
    const current = yearMap[String(monthIdx + 1)] || "";
    const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length];
    try {
      await api.patch(`/admin/customers/${customerId}/services/${idx}/monthly`, {
        year, month: monthIdx + 1, status: next,
      });
      onChange();
    } catch { toast.error("Failed to update month"); }
  };

  // Count completed months for current year
  const monthCounts = MONTHS.reduce((acc, _, i) => {
    const s = yearMap[String(i + 1)];
    if (s) acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="bento-card overflow-hidden" data-testid={`service-card-${idx}`}>
      {/* Header */}
      <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-[#E5E2DC] flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className="label-eyebrow text-[10px]">Service #{idx + 1}</span>
          <h3 className="mt-1 text-xl font-display font-medium text-[#121C17]">{svc.service_name}</h3>
          <p className="text-xs text-[#5C6661] mt-1">Started {new Date(svc.started_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · ₹{(svc.price || 0).toLocaleString("en-IN")}</p>
        </div>
        <div className="flex gap-2">
          <a
            data-testid={`wa-share-creds-${idx}`}
            href={waToCustomer(customer?.whatsapp || customer?.mobile, buildCredentialsMessage(customer, svc, creds))}
            target="_blank" rel="noopener noreferrer"
            className="text-xs text-white inline-flex items-center gap-1 px-3 py-1.5 rounded-full hover:opacity-90"
            style={{ background: "#25D366" }}
            title="Share login details to customer on WhatsApp"
          >
            <Send size={12} /> Share creds
          </a>
          <button onClick={onRemove} data-testid={`service-remove-${idx}`} className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-100 hover:border-red-300">
            <Trash2 size={12} /> Remove
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 p-5 sm:p-6">
        {/* CREDENTIALS COLUMN */}
        <div data-testid={`service-creds-${idx}`}>
          <h4 className="label-eyebrow text-[10px] mb-3">Login credentials</h4>
          <div className="space-y-3">
            <div>
              <label className="label-eyebrow text-[10px]">Username / Login ID</label>
              <div className="mt-2 flex gap-2">
                <input value={creds.service_username} onChange={(e) => setCreds({ ...creds, service_username: e.target.value })} data-testid={`cred-username-${idx}`}
                  className="flex-1 bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 text-sm focus:border-[#1E4D3B] font-mono" />
                <button onClick={() => copyToClipboard(creds.service_username, "Username")} data-testid={`copy-username-${idx}`}
                  className="rounded-xl px-3 border border-[#E5E2DC] hover:bg-[#F3F1EC]" aria-label="Copy username"><Copy size={14} /></button>
              </div>
            </div>
            <div>
              <label className="label-eyebrow text-[10px]">Password</label>
              <div className="mt-2 flex gap-2">
                <input type={showPw ? "text" : "password"} value={creds.service_password} onChange={(e) => setCreds({ ...creds, service_password: e.target.value })} data-testid={`cred-password-${idx}`}
                  className="flex-1 bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 text-sm focus:border-[#1E4D3B] font-mono" />
                <button onClick={() => setShowPw(!showPw)} data-testid={`toggle-password-${idx}`}
                  className="rounded-xl px-3 border border-[#E5E2DC] hover:bg-[#F3F1EC]" aria-label="Show/hide password">
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button onClick={() => copyToClipboard(creds.service_password, "Password")} data-testid={`copy-password-${idx}`}
                  className="rounded-xl px-3 border border-[#E5E2DC] hover:bg-[#F3F1EC]" aria-label="Copy password"><Copy size={14} /></button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow text-[10px]">Status</label>
                <select value={creds.status} onChange={(e) => setCreds({ ...creds, status: e.target.value })} data-testid={`cred-status-${idx}`}
                  className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B]">
                  {["Active", "Renewal Pending", "Completed", "Paused"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="label-eyebrow text-[10px]">Renewal date</label>
                <input type="date" value={creds.renewal_date || ""} onChange={(e) => setCreds({ ...creds, renewal_date: e.target.value })} data-testid={`cred-renewal-${idx}`}
                  className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B]" />
              </div>
            </div>
            <div>
              <label className="label-eyebrow text-[10px]">Internal notes</label>
              <textarea rows={2} value={creds.notes} onChange={(e) => setCreds({ ...creds, notes: e.target.value })} data-testid={`cred-notes-${idx}`}
                placeholder="Portal URL, security questions, etc." className="mt-2 w-full bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B]" />
            </div>
            <button onClick={saveCreds} disabled={saving} data-testid={`cred-save-${idx}`}
              className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
              <Save size={14} /> {saving ? "Saving…" : "Save credentials"}
            </button>
          </div>
        </div>

        {/* MONTHLY GRID COLUMN */}
        <div data-testid={`service-monthly-${idx}`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className="label-eyebrow text-[10px]">Monthly work status</h4>
            <div className="flex items-center gap-1">
              <button onClick={() => setYear(year - 1)} className="w-7 h-7 rounded-full border border-[#E5E2DC] grid place-items-center hover:bg-[#F3F1EC]" data-testid={`year-prev-${idx}`} aria-label="Previous year"><ChevronLeft size={14} /></button>
              <span className="text-sm font-medium px-3 text-[#121C17]" data-testid={`year-label-${idx}`}>{year}</span>
              <button onClick={() => setYear(year + 1)} className="w-7 h-7 rounded-full border border-[#E5E2DC] grid place-items-center hover:bg-[#F3F1EC]" data-testid={`year-next-${idx}`} aria-label="Next year"><ChevronRight size={14} /></button>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-2 mb-3 text-[11px]">
            {Object.entries(STATUS_META).map(([k, m]) => {
              const I = m.icon;
              return (
                <span key={k} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${m.cls}`}>
                  <I size={10} /> {m.label}
                </span>
              );
            })}
          </div>

          {/* 12-Month Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`monthly-grid-${idx}`}>
            {MONTHS.map((m, i) => {
              const st = yearMap[String(i + 1)];
              const meta = STATUS_META[st];
              const Icon = meta?.icon;
              return (
                <button key={m} onClick={() => cycleMonth(i)} data-testid={`month-${idx}-${year}-${i + 1}`}
                  className={`rounded-xl border p-3 text-left transition-all hover:scale-[1.02] hover:shadow-sm ${
                    meta ? meta.cls : "bg-[#FDFCFB] border-dashed border-[#E5E2DC] text-[#5C6661]"
                  }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold">{m}</span>
                    {Icon && <Icon size={12} />}
                  </div>
                  <div className="mt-1 text-[10px] font-medium opacity-80 truncate">{meta?.label || "Not set"}</div>
                </button>
              );
            })}
          </div>

          {/* Summary counts */}
          <div className="mt-4 pt-4 border-t border-[#E5E2DC] flex flex-wrap gap-2 text-[11px]" data-testid={`monthly-summary-${idx}`}>
            {Object.entries(STATUS_META).map(([k, m]) => (
              <span key={k} className="inline-flex items-center gap-1 text-[#5C6661]">
                <span className="w-2 h-2 rounded-full" style={{ background: k === "Done" ? "#10b981" : k === "Pending" ? "#f59e0b" : k === "Not Done" ? "#ef4444" : "#94a3b8" }} />
                {m.label}: <span className="font-medium text-[#121C17]">{monthCounts[k] || 0}</span>
              </span>
            ))}
            <span className="ml-auto text-[#5C6661]">Tip: click a month to cycle status</span>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// WhatsApp message builders
// ============================================================================
function buildCredentialsMessage(customer, svc, creds) {
  const name = customer?.full_name || "there";
  const username = (creds?.service_username ?? svc.service_username) || "—";
  const password = (creds?.service_password ?? svc.service_password) || "—";
  const status = (creds?.status ?? svc.status) || "Active";
  const renewal = (creds?.renewal_date ?? svc.renewal_date) || null;
  const notes = (creds?.notes ?? svc.notes) || "";

  let msg = `Hello ${name},\n\nHere are your *${svc.service_name}* account details with FinSahayak:\n\n`;
  msg += `🔑 Username: ${username}\n`;
  msg += `🔐 Password: ${password}\n`;
  msg += `📌 Status: ${status}\n`;
  if (renewal) msg += `📅 Renewal: ${renewal}\n`;
  if (notes) msg += `\n📝 Notes: ${notes}\n`;
  msg += `\nPlease keep these confidential. Reply to this chat if anything is unclear.\n\n— Team FinSahayak`;
  return msg;
}

function buildDueMessage(customer) {
  const name = customer?.full_name || "there";
  const due = customer?.total_due || 0;
  const paid = customer?.total_paid || 0;

  if (due <= 0) {
    return `Hello ${name},\n\nGood news — your account with FinSahayak shows *no outstanding balance*. ✅\n\nTotal paid so far: ₹${paid.toLocaleString("en-IN")}\n\nThank you for being a wonderful customer!\n\n— Team FinSahayak`;
  }

  let msg = `Hello ${name},\n\nFriendly reminder from *FinSahayak* — your account has an outstanding balance.\n\n`;
  msg += `💰 *Outstanding: ₹${due.toLocaleString("en-IN")}*\n`;
  msg += `✅ Paid so far: ₹${paid.toLocaleString("en-IN")}\n\n`;
  msg += `You can settle the dues via UPI, bank transfer or Razorpay. Reply to this chat once paid and we'll send the receipt.\n\n— Team FinSahayak`;
  return msg;
}

function buildAccountSummaryMessage(customer) {
  const name = customer?.full_name || "there";
  let msg = `Hello ${name},\n\nHere's a quick summary of your FinSahayak account:\n\n`;
  msg += `🆔 Customer ID: ${customer?.ref}\n`;
  msg += `📞 Mobile: ${customer?.mobile}\n`;
  msg += `📧 Email: ${customer?.email}\n\n`;
  msg += `*Services:*\n`;
  (customer?.service_history || []).forEach((s, i) => {
    msg += `${i + 1}. ${s.service_name} — ${s.status || "Active"} (₹${(s.price || 0).toLocaleString("en-IN")})\n`;
  });
  msg += `\n💰 Total Paid: ₹${(customer?.total_paid || 0).toLocaleString("en-IN")}`;
  msg += `\n📌 Outstanding: ₹${(customer?.total_due || 0).toLocaleString("en-IN")}\n`;
  msg += `\nReply for any clarification.\n\n— Team FinSahayak`;
  return msg;
}


function buildPaymentLinkMessage(customer, paymentLink) {
  const name = customer?.full_name || "there";
  const due = customer?.total_due || 0;
  const ref = customer?.ref || "";
  let msg = `Hello ${name},\n\n`;
  msg += `Greetings from *FinSahayak* 🙏\n\n`;
  if (ref) msg += `Reference: *${ref}*\n`;
  msg += `Amount payable: *₹${due.toLocaleString("en-IN")}*\n\n`;
  msg += `🔒 Secure payment link (Razorpay):\n${paymentLink || "[link]"}\n\n`;
  msg += `Pay using UPI, cards, netbanking or wallets. Once your payment is successful, your receipt is recorded automatically and our team is notified instantly.\n\n`;
  msg += `For any help, just reply to this message.\n\n— Team FinSahayak\nfinsahayak.com`;
  return msg;
}

// ============================================================================
// Khatabook — running ledger of charges + payments
// ============================================================================
function buildLedger(customer) {
  const events = [];
  (customer?.service_history || []).forEach((s, i) => {
    events.push({
      id: s.id || `svc-${i}`,
      date: s.started_at,
      kind: "debit",
      label: `${s.service_name} added`,
      sublabel: s.status ? `Status: ${s.status}` : "",
      amount: s.price || 0,
    });
  });
  (customer?.payments || []).forEach((p) => {
    events.push({
      id: p.id,
      date: p.created_at,
      kind: "credit",
      label: `Payment received`,
      sublabel: `${p.method}${p.note ? ` · ${p.note}` : ""}`,
      amount: p.amount,
    });
  });
  events.sort((a, b) => new Date(a.date) - new Date(b.date));
  let balance = 0;
  const billed = events.filter((e) => e.kind === "debit").reduce((s, e) => s + e.amount, 0);
  const paid = events.filter((e) => e.kind === "credit").reduce((s, e) => s + e.amount, 0);
  const rows = events.map((e) => {
    balance += e.kind === "debit" ? e.amount : -e.amount;
    return { ...e, balance };
  });
  return { rows, billed, paid, balance };
}

function KhatabookSection({ customer }) {
  const { rows, billed, paid, balance } = buildLedger(customer);
  if (rows.length === 0) {
    return (
      <div className="bento-card p-6 mb-6" data-testid="khatabook-section">
        <div className="flex items-center gap-2 mb-2"><BookOpen size={16} className="text-[#1E4D3B]" /><h3 className="font-display font-medium text-lg text-[#121C17]">Khatabook</h3></div>
        <p className="text-sm text-[#5C6661]">No transactions yet. Add a service or record a payment to populate the ledger.</p>
      </div>
    );
  }
  return (
    <div className="bento-card p-5 sm:p-6 mb-6" data-testid="khatabook-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#1E4D3B]/10 grid place-items-center text-[#1E4D3B]"><BookOpen size={16} /></div>
          <div>
            <span className="label-eyebrow text-[10px]">Customer Khatabook</span>
            <h3 className="font-display font-medium text-lg text-[#121C17] -mt-0.5">Running ledger</h3>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3 text-right text-xs sm:text-sm w-full sm:w-auto">
          <div><div className="text-[10px] uppercase tracking-wider text-[#5C6661]">Billed</div><div className="font-display font-medium text-[#121C17]">₹{billed.toLocaleString("en-IN")}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#5C6661]">Paid</div><div className="font-display font-medium text-emerald-700">₹{paid.toLocaleString("en-IN")}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#5C6661]">Balance</div><div className={`font-display font-medium ${balance > 0 ? "text-amber-700" : "text-[#1E4D3B]"}`}>₹{balance.toLocaleString("en-IN")}</div></div>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto" data-testid="khatabook-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#5C6661] border-b border-[#E5E2DC]">
              <th className="py-3 pr-4">Date</th>
              <th className="py-3 pr-4">Particulars</th>
              <th className="py-3 pr-4 text-right">Debit</th>
              <th className="py-3 pr-4 text-right">Credit</th>
              <th className="py-3 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#F3F1EC] last:border-0" data-testid={`ledger-row-${r.id}`}>
                <td className="py-3 pr-4 text-xs text-[#5C6661] whitespace-nowrap">{new Date(r.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}</td>
                <td className="py-3 pr-4">
                  <div className={`inline-flex items-center gap-2 ${r.kind === "debit" ? "text-[#121C17]" : "text-emerald-700"}`}>
                    {r.kind === "debit" ? <TrendingUp size={13} className="text-amber-600" /> : <TrendingDown size={13} className="text-emerald-600" />}
                    <div>
                      <div className="font-medium">{r.label}</div>
                      {r.sublabel && <div className="text-[11px] text-[#5C6661]">{r.sublabel}</div>}
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-4 text-right font-mono text-amber-700">{r.kind === "debit" ? `₹${r.amount.toLocaleString("en-IN")}` : ""}</td>
                <td className="py-3 pr-4 text-right font-mono text-emerald-700">{r.kind === "credit" ? `₹${r.amount.toLocaleString("en-IN")}` : ""}</td>
                <td className="py-3 text-right font-mono font-medium text-[#121C17]">₹{r.balance.toLocaleString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="md:hidden divide-y divide-[#F3F1EC]" data-testid="khatabook-mobile-list">
        {rows.map((r) => (
          <li key={r.id} className="py-3 flex items-start gap-3" data-testid={`ledger-card-${r.id}`}>
            <div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${r.kind === "debit" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
              {r.kind === "debit" ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-sm font-medium text-[#121C17] truncate">{r.label}</div>
                <div className={`text-sm font-mono font-medium ${r.kind === "debit" ? "text-amber-700" : "text-emerald-700"}`}>
                  {r.kind === "debit" ? "+" : "−"}₹{r.amount.toLocaleString("en-IN")}
                </div>
              </div>
              <div className="flex items-baseline justify-between gap-2 mt-0.5">
                <div className="text-[11px] text-[#5C6661] truncate">{new Date(r.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })} {r.sublabel && `· ${r.sublabel}`}</div>
                <div className="text-[11px] text-[#5C6661]">Bal ₹{r.balance.toLocaleString("en-IN")}</div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
